import { TokenStats } from '@/pages/api/tokens/info';
import { format } from 'date-fns';

import { fontStyles } from '@/styles/typography';

import { ClockIcon } from '@/ui/Icons/ClockIcon';
import { SmallDotIcon } from '@/ui/Icons/SmallDotIcon';
import {
  MatchaTooltip,
  MatchaTooltipBottomSheet,
  MatchaTooltipContent,
  MatchaTooltipPopover,
  MatchaTooltipTrigger,
} from '@/ui/MatchaTooltip';

import { TokenResult } from '@/utils/0x/token-registry.types';
import { isTokenResultNativeAsset } from '@/utils/multichain';
import { formatTimeElapsed } from '@/utils/numbers';
import { ZoraType } from '@/utils/zora';

import { LiquidityScore } from '../TokenLiquidityScore';
import { TokenProfileContractAddress } from '../TokenProfileContractAddress';
import { SecurityAuditPreview } from '../TokenProfileSecurityAudit/SecurityAuditPreview';

interface TokenSecondaryInfoProps {
  token: TokenResult;
  zoraType?: ZoraType;
  stats?: TokenStats;
}

export const TokenSecondaryInfo = ({ token, zoraType, stats }: TokenSecondaryInfoProps) => {
  const showTokenAddress = !isTokenResultNativeAsset(token);

  const creationTime = stats?.creationTime;

  return (
    <div
      className={fontStyles({
        variant: 'BODY_MD',
        class: 'flex items-center gap-2 flex-row w-full ml-1 text-token-gray-800',
      })}
    >
      <span className="hidden tablet:inline">{token.chainName}</span>
      {showTokenAddress && (
        <>
          <SmallDotIcon className="hidden tablet:inline text-token-gray-500" />
          <TokenProfileContractAddress token={token} className="-ml-1" />
        </>
      )}
      <SmallDotIcon
        className={
          showTokenAddress
            ? '-ml-1 text-token-gray-500'
            : 'hidden tablet:inline -ml-1 text-token-gray-500'
        }
      />
      {creationTime != null && (
        <>
          <MatchaTooltip>
            <MatchaTooltipTrigger>
              <button type="button" className="flex flex-row items-center gap-1 cursor-default">
                <ClockIcon className="text-token-gray-700" />
                {formatTimeElapsed(creationTime, true)}
              </button>
            </MatchaTooltipTrigger>
            <MatchaTooltipContent>
              {format(new Date(creationTime * 1000), 'Pp')}
            </MatchaTooltipContent>
            <MatchaTooltipPopover sideOffset={8} />
            <MatchaTooltipBottomSheet header="Token creation time" />
          </MatchaTooltip>
          <SmallDotIcon className="text-token-gray-500" />
        </>
      )}
      <div className="inline-flex flex-row gap-1">
        <SecurityAuditPreview activeToken={token} />
        <LiquidityScore activeToken={token} zoraType={zoraType} />
      </div>
    </div>
  );
};
